/**
 * Global API utility for consistent fetch configuration
 * Ensures all API calls include credentials for session management
 */

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

interface FetchOptions extends RequestInit {
  skipCredentials?: boolean;
}

/**
 * Wrapper around fetch that automatically includes credentials
 * and handles common API patterns
 */
export async function apiFetch(
  endpoint: string,
  options: FetchOptions = {}
): Promise<Response> {
  const { skipCredentials, ...fetchOptions } = options;

  // Build full URL
  // If endpoint already starts with http:// or https://, use it as-is
  // Otherwise, append to API_BASE
  let url: string;
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    url = endpoint;
  } else {
    // Ensure endpoint starts with /
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    // Remove /api prefix from endpoint if it exists (since API_BASE already includes it)
    const cleanPath = path.startsWith('/api') ? path.substring(4) : path;
    url = `${API_BASE}${cleanPath}`;
  }

  // Default options
  const defaultOptions: RequestInit = {
    credentials: skipCredentials ? 'same-origin' : 'include',
  };

  // Merge options
  const finalOptions = {
    ...defaultOptions,
    ...fetchOptions,
  };

  // Add Content-Type for JSON payloads
  if (finalOptions.body && typeof finalOptions.body === 'string') {
    finalOptions.headers = {
      'Content-Type': 'application/json',
      ...finalOptions.headers,
    };
  }

  return fetch(url, finalOptions);
}

/**
 * Convenience method for GET requests
 */
export async function apiGet(endpoint: string, options?: FetchOptions) {
  return apiFetch(endpoint, { ...options, method: 'GET' });
}

/**
 * Convenience method for POST requests with JSON body
 */
export async function apiPost(
  endpoint: string,
  body?: any,
  options?: FetchOptions
) {
  return apiFetch(endpoint, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Convenience method for DELETE requests
 */
export async function apiDelete(endpoint: string, options?: FetchOptions) {
  return apiFetch(endpoint, { ...options, method: 'DELETE' });
}

/**
 * Convenience method for file uploads
 */
export async function apiUpload(
  endpoint: string,
  file: File,
  options?: FetchOptions
) {
  const formData = new FormData();
  formData.append('file', file);

  return apiFetch(endpoint, {
    ...options,
    method: 'POST',
    body: formData,
    // Don't set Content-Type for FormData - browser will set it with boundary
  });
}